import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import UserSidebar from '../../components/sidebar/UserSidebar';
import { RootState } from '../../redux/store';
import { kycApi, paymentsApi, ratesApi } from '../../services/api';
import { KycDocument, PaymentRequest } from '../../types/customer';
import { getDocumentTypeLabel } from '../../utils/documentTypeMap';
import EnterpriseSpendChart from '../../components/dashboard/EnterpriseSpendChart';
import './HomePage.css';

const HomePage: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const isApproved = user?.accountStatus === 'approved';

  const [rate, setRate] = useState<number | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string>('');
  const [rateLoading, setRateLoading] = useState<boolean>(true);
  const [rateError, setRateError] = useState<string>('');

  const [statsLoading, setStatsLoading] = useState<boolean>(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);

  const [feedbackLoading, setFeedbackLoading] = useState<boolean>(true);
  const [feedbackError, setFeedbackError] = useState<string>('');
  const [rejectedPayments, setRejectedPayments] = useState<PaymentRequest[]>([]);
  const [rejectedDocuments, setRejectedDocuments] = useState<KycDocument[]>([]);
  const [spendingTrend, setSpendingTrend] = useState<Array<{ label: string; spend: number; outflow: number }>>([]);

  const buildSixMonthTrend = useCallback((payments: PaymentRequest[]) => {
    const now = new Date();
    const outflowStatuses = new Set(['approved', 'processing', 'completed']);
    const months = Array.from({ length: 6 }, (_, index) => {
      const date = new Date(now.getFullYear(), now.getMonth() - (5 - index), 1);
      return {
        key: `${date.getFullYear()}-${date.getMonth()}`,
        label: date.toLocaleString('en-US', { month: 'short' }),
        spend: 0,
        outflow: 0,
      };
    });

    payments.forEach((payment) => {
      if (!payment.createdAt) return;
      const createdAt = new Date(payment.createdAt);
      if (Number.isNaN(createdAt.getTime())) return;

      const monthKey = `${createdAt.getFullYear()}-${createdAt.getMonth()}`;
      const bucket = months.find((month) => month.key === monthKey);
      if (!bucket) return;

      const amount = Number(payment.localAmount || 0);
      bucket.spend += amount;
      if (outflowStatuses.has(payment.status)) {
        bucket.outflow += amount;
      }
    });

    return months;
  }, []);

  const fetchRate = useCallback(async () => {
    try {
      setRateLoading(true);
      setRateError('');
      const data = (await ratesApi.getUsdNgnRate()) as any;
      setRate(data.usdToNgnRate);
      setUpdatedAt(new Date(data.updatedAt).toLocaleString());
    } catch (error) {
      console.error('Error fetching rate:', error);
      setRateError('Unable to load exchange rate');
    } finally {
      setRateLoading(false);
    }
  }, []);

  const fetchPaymentStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const response = await paymentsApi.getMyPayments();
      const paymentsList = response.payments || [];

      const pending = paymentsList.filter((payment: any) => payment.status === 'pending_admin_approval').length;
      const completed = paymentsList.filter((payment: any) => payment.status === 'completed').length;

      setPendingCount(pending);
      setCompletedCount(completed);
      setSpendingTrend(buildSixMonthTrend(paymentsList));
    } catch (error) {
      console.error('Error fetching payment stats:', error);
      setPendingCount(0);
      setCompletedCount(0);
      setSpendingTrend(buildSixMonthTrend([]));
    } finally {
      setStatsLoading(false);
    }
  }, [buildSixMonthTrend]);

  const fetchFeedback = useCallback(async () => {
    try {
      setFeedbackLoading(true);
      setFeedbackError('');

      const [paymentsResponse, documentsResponse] = await Promise.all([
        paymentsApi.getMyPayments(),
        kycApi.getMyDocuments(),
      ]);

      const rejectedPaymentItems = (paymentsResponse.payments || []).filter(
        (payment) => payment.status === 'rejected' && Boolean(payment.rejectionReason?.trim())
      );
      const rejectedDocumentItems = (documentsResponse.documents || []).filter(
        (document) => document.status === 'rejected' && Boolean(document.rejectionReason?.trim())
      );

      setRejectedPayments(rejectedPaymentItems);
      setRejectedDocuments(rejectedDocumentItems);
    } catch (error) {
      console.error('Error fetching feedback:', error);
      setFeedbackError('Unable to load feedback notifications');
      setRejectedPayments([]);
      setRejectedDocuments([]);
    } finally {
      setFeedbackLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRate();
    fetchPaymentStats();
    fetchFeedback();

    const interval = setInterval(fetchRate, 300000);
    return () => clearInterval(interval);
  }, [fetchFeedback, fetchPaymentStats, fetchRate]);

  return (
    <DashboardLayout sidebar={<UserSidebar />}>
      <div className="homepage">
        <div className="dashboard-welcome">
          <h2>Welcome, {user?.name}!</h2>
          <p className="welcome-subtitle">Manage your international payments and account settings</p>

          <div className="welcome-cards">
            <Link to="/dashboard/my-payments" className="welcome-card">
              <div className="card-icon">Payments</div>
              <h3>My Payments</h3>
              <p>View all your submitted payments and their status</p>
            </Link>

            <Link to="/dashboard/documents" className="welcome-card">
              <div className="card-icon">KYC</div>
              <h3>Document Upload</h3>
              <p>Submit required documents for account verification</p>
            </Link>

            <Link to="/dashboard/payments" className="welcome-card">
              <div className="card-icon">Send</div>
              <h3>Payment Submission</h3>
              <p>Submit international payment requests for approval</p>
            </Link>

            <Link to="/dashboard/profile" className="welcome-card">
              <div className="card-icon">Profile</div>
              <h3>Profile Management</h3>
              <p>Update your profile information and settings</p>
            </Link>
          </div>

          <div className="quick-stats">
            <h3>Quick Stats</h3>
            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-number">{statsLoading ? '...' : pendingCount}</span>
                <span className="stat-label">Pending Payments</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{statsLoading ? '...' : completedCount}</span>
                <span className="stat-label">Completed Transactions</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Approval Status</span>
                <span className={`stat-status ${isApproved ? 'approved' : 'pending'}`}>
                  {isApproved ? 'Approved' : 'Pending'}
                </span>
              </div>
            </div>
          </div>

          <EnterpriseSpendChart
            title="Spending Over Time"
            subtitle="Track submitted spend versus wallet outflow for the last 6 months."
            points={spendingTrend}
            currencyCode="NGN"
          />

          <div className="feedback-widget">
            <div className="feedback-header">
              <h3>Feedback / Notifications</h3>
              <span className="feedback-count">
                {rejectedPayments.length + rejectedDocuments.length} item(s)
              </span>
            </div>

            {feedbackLoading && <p className="feedback-meta">Loading feedback...</p>}
            {!feedbackLoading && feedbackError && <p className="feedback-error">{feedbackError}</p>}
            {!feedbackLoading &&
              !feedbackError &&
              rejectedPayments.length === 0 &&
              rejectedDocuments.length === 0 && (
                <p className="feedback-meta">No rejection feedback yet.</p>
              )}

            {!feedbackLoading &&
              !feedbackError &&
              (rejectedPayments.length > 0 || rejectedDocuments.length > 0) && (
                <div className="feedback-lists">
                  {rejectedPayments.length > 0 && (
                    <div className="feedback-section">
                      <h4>Payment Requests</h4>
                      <ul>
                        {rejectedPayments.map((payment) => (
                          <li key={payment._id}>
                            <p>
                              <strong>{payment.recipientCompany}</strong>: {payment.rejectionReason}
                            </p>
                            <Link to="/dashboard/my-payments">View My Payments</Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {rejectedDocuments.length > 0 && (
                    <div className="feedback-section">
                      <h4>KYC Documents</h4>
                      <ul>
                        {rejectedDocuments.map((document) => (
                          <li key={document._id}>
                            <p>
                              <strong>{getDocumentTypeLabel(document.type)}</strong>: {document.rejectionReason}
                            </p>
                            <Link to="/dashboard/documents">View Documents</Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
          </div>
        </div>

        <div className="rate-display">
          <h3>USDC to NGN Exchange Rate</h3>
          {rateLoading && <p>Loading rate...</p>}
          {!rateLoading && rateError && <p className="error-message">{rateError}</p>}
          {!rateLoading && !rateError && (
            <div>
              <p className="rate-value">1 USDC = NGN {rate?.toLocaleString()}</p>
              <p className="rate-subtitle">Rate set by platform admin</p>
              {updatedAt && <p className="rate-updated">Last Updated: {updatedAt}</p>}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default HomePage;
